import { Component, Inject, OnInit, AfterViewInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { Order } from 'src/app/models/order';
import { ORDER_STATUS, orderStatusForInvoice, orderStatusKeysConst, canCancelByAdmin } from 'src/app/core/const/orderStatusKey.const';
import { SocketService } from 'src/app/core/services/socket.service';
import { Subject, takeUntil } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { InvoiceComponent } from 'src/app/modals/invoice/invoice.component';
import { OrderService } from 'src/app/core/services/order.service';
import { SpinnerService } from 'src/app/core/services/spinner.service';
import { numberConst } from 'src/app/core/const/number.const';
import { orderUpdatePayload } from 'src/app/models/orderPayload';
import { OrderCancelDialogService } from 'src/app/core/services/order-cancel-dialog.service';
import { ExpectedDeliveryDateComponent } from 'src/app/modals/expected-delivery-date/expected-delivery-date.component';
interface StatusHistoryItem {
  status: number;
  createdAt: string;
  statusName: string;
  isCompleted: boolean;
  isUpcoming: boolean;
  isCurrent: boolean;
  isRejected: boolean;
  index: number;
}

@Component({
  selector: 'megastore-admin-order-details',
  templateUrl: './order-details.component.html',
  styleUrls: ['./order-details.component.scss']
})
export class OrderDetailsComponent implements OnInit, AfterViewInit {
  orderStatus = ORDER_STATUS;
  orderStatusTracking = ORDER_STATUS;
  orderStatusKeysConst = orderStatusKeysConst;
  canCancelByAdmin = canCancelByAdmin;
  statusHistory: StatusHistoryItem[] = [];
  private destroy$ = new Subject<void>();
  showUpcomingStatuses = true;

  orderStatusForInvoice = orderStatusForInvoice;
  
  // Image gallery properties
  selectedImage: string = '';
  currentImageIndex: number = 0;

  // Access control - default to true if not provided
  moduleAccessTypes: { isEdit: boolean } = { isEdit: true };
  nextStatus: number = 0;
  isReturnOrder: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: {data: Order; moduleAccessTypes?: { isEdit: boolean }; nextStatus?: number; isReturnOrder?: boolean},
    private socketService: SocketService,
    private dialog: MatDialog,
    private _orderService: OrderService,
    private dialogRef: MatDialogRef<OrderDetailsComponent>,
    private _spinnerService: SpinnerService,
    private _orderCancelDialogService: OrderCancelDialogService
  ) {
    this.initializeStatusHistory();
    if (this.data.moduleAccessTypes) {
      this.moduleAccessTypes = this.data.moduleAccessTypes;
    }
    if (this.data.nextStatus !== undefined) {
      this.nextStatus = this.data.nextStatus;
    }
    if (this.data.isReturnOrder !== undefined) {
      this.isReturnOrder = this.data.isReturnOrder;
    }
  }

  ngOnInit() {
    // Initialize image gallery
    this.initializeImageGallery();
    
    // Listen for order status updates
    this.socketService.onEvent('updatedOrderStatus')
      .pipe(takeUntil(this.destroy$))
      .subscribe((updatedOrder: Order) => {
        if (updatedOrder._id === this.data.data._id) {
          this.updateStatusHistory(updatedOrder);
        }
      });
  }

  initializeImageGallery(): void {
    const images = this.getProductImages();
    if (images.length > 0) {
      this.selectedImage = images[0];
      this.currentImageIndex = 0;
    }
  }

  getProductImages(): string[] {
    const images: string[] = [];
    
    // Add variant images if available
    if (this.data.data.productVariant?.image && Array.isArray(this.data.data.productVariant.image)) {
      images.push(...this.data.data.productVariant.image);
    }
    
    // Add product image if available and not already in array
    if (this.data.data.product?.image && !images.includes(this.data.data.product.image)) {
      images.unshift(this.data.data.product.image);
    }
    
    return images.length > 0 ? images : ['assets/images/product-placeholder.png'];
  }

  selectImage(index: number): void {
    this.currentImageIndex = index;
    this.selectedImage = this.getProductImages()[index];
  }

  previousImage(): void {
    if (this.currentImageIndex > 0) {
      this.currentImageIndex--;
      this.selectedImage = this.getProductImages()[this.currentImageIndex];
    }
  }

  nextImage(): void {
    const images = this.getProductImages();
    if (this.currentImageIndex < images.length - 1) {
      this.currentImageIndex++;
      this.selectedImage = images[this.currentImageIndex];
    }
  }

  openFactoryMap(): void {
    if (this.data.data.factory?.latitude && this.data.data.factory?.longitude) {
      window.open(`https://www.google.com/maps?q=${this.data.data.factory.latitude},${this.data.data.factory.longitude}`, '_blank');
    }
  }

  ngAfterViewInit() {
    // Set CSS custom properties for animation delays
    this.statusHistory.forEach((item, index) => {
      const element = document.querySelector(`.timeline-item:nth-child(${index + 1})`);
      if (element) {
        (element as HTMLElement).style.setProperty('--index', index.toString());
      }
    });
  }

  private initializeStatusHistory() {
    if (this.data.data.statusHistory) {
      // Define all possible statuses in order
      let allStatuses = [
        this.orderStatusTracking.PENDING,
        this.orderStatusTracking.ACCEPTED,
        this.orderStatusTracking.READY_TO_PICK,
        this.orderStatusTracking.ACCEPTED_BY_DRIVER,
        this.orderStatusTracking.REACHED_PICKUP_LOCATION,
        this.orderStatusTracking.ORDER_PICKED_UP,
        this.orderStatusTracking.ONTHEWAY,
        this.orderStatusTracking.DELIVERED
      ];

      // Define cancelled statuses
      const cancelledStatuses = [
        this.orderStatusTracking.CANCEL_BY_USER,
        this.orderStatusTracking.CANCEL_BY_ADMIN,
        this.orderStatusTracking.RETURN_REQUESTED,
        this.orderStatusTracking.UNDELIVERED_ORDER_RETURNED_TO_FACTORY,
        this.orderStatusTracking.UNDELIVERED,
      ];

      // Filter statuses if order is cancelled
      allStatuses = !cancelledStatuses.includes(this.data.data.status) 
        ? allStatuses 
        : this.data.data.statusHistory.map(item => item.status);

      // Create status history with all statuses
      this.statusHistory = allStatuses.map((status, index) => {
        const existingStatus = this.data.data.statusHistory?.find(
          item => item.status === status
        );

        const isCompleted = status <= this.data.data.status;
        const isCurrent = status === this.data.data.status;
        const isUpcoming = !cancelledStatuses.includes(this.data.data.status) && 
                          !isCompleted && 
                          index === allStatuses.findIndex(s => s > this.data.data.status);

        if (existingStatus) {
          return {
            ...existingStatus,
            statusName: this.getStatusName(existingStatus.status),
            isCompleted,
            isUpcoming,
            isCurrent,
            isRejected: cancelledStatuses.includes(existingStatus.status),
            index
          };
        } else {
          return {
            status,
            createdAt: new Date().toISOString(),
            statusName: this.getStatusName(status),
            isCompleted,
            isUpcoming,
            isCurrent,
            isRejected: false,
            index
          };
        }
      });

      // Handle canceled by user case
      if (this.data.data.status === this.orderStatusTracking.CANCEL_BY_USER) {
        const canceledByUser = this.data.data.statusHistory?.find(
          item => item.status === this.orderStatusTracking.UNDELIVERED_ORDER_RETURNED_TO_FACTORY
        );
        if (canceledByUser) {
          this.statusHistory = this.statusHistory.filter(
            item => item.status !== this.orderStatusTracking.UNDELIVERED_ORDER_RETURNED_TO_FACTORY
          );
          this.statusHistory.push({
            status: this.orderStatusTracking.CANCEL_BY_USER,
            createdAt: canceledByUser.createdAt,
            statusName: this.getStatusName(this.orderStatusTracking.CANCEL_BY_USER),
            isCompleted: true,
            isUpcoming: false,
            isCurrent: true,
            isRejected: true,
            index: this.statusHistory.length
          });
        }
      }

      // Handle return request case
      const requestReturn = this.data.data.statusHistory?.find(
        item => item.status === this.orderStatusTracking.RETURN_REQUESTED
      );

      if (requestReturn) {
        this.statusHistory.push({
          status: requestReturn.status,
          createdAt: requestReturn.createdAt,
          statusName: this.getStatusName(requestReturn.status),
          isCompleted: true,
          isUpcoming: false,
          isRejected: true,
          isCurrent: requestReturn.status === this.data.data.status,
          index: this.statusHistory.length
        });
      }

      // Update showUpcomingStatuses flag
      this.showUpcomingStatuses = !cancelledStatuses.includes(this.data.data.status);
    }
  }

  private updateStatusHistory(updatedOrder: Order) {
    if (updatedOrder.statusHistory) {
      const newStatus = updatedOrder.statusHistory[updatedOrder.statusHistory.length - 1];
      const statusIndex = this.statusHistory.findIndex(item => item.status === newStatus.status);
      
      if (statusIndex !== -1) {
        // Update the status
        this.statusHistory[statusIndex].isCompleted = true;
        this.statusHistory[statusIndex].isUpcoming = false;
        this.statusHistory[statusIndex].isCurrent = true;

        // Check if we should hide upcoming statuses
        const negativeStatuses = [this.orderStatusTracking.CANCEL_BY_USER, this.orderStatusTracking.CANCEL_BY_ADMIN];
        if (negativeStatuses.includes(newStatus.status)) {
          this.showUpcomingStatuses = false;
          this.statusHistory.forEach(item => {
            if (item.isUpcoming) {
              item.isUpcoming = false;
            }
          });
        }

        // Update upcoming status
        if (this.showUpcomingStatuses) {
          const nextStatusIndex = this.statusHistory.findIndex(item => item.status > newStatus.status);
          if (nextStatusIndex !== -1) {
            this.statusHistory[nextStatusIndex].isUpcoming = true;
          }
        }
      }
    }
  }

  getStatusName(status: number): string {
    for (const [key, value] of Object.entries(this.orderStatus)) {
      if (value === status) {
        return key;
      }
    }
    return 'Unknown';
  }

  downloadInvoice(): void {
    this._orderService.invoice(this.data.data._id).subscribe((res: any) => {
      window.open(res.data, '_blank');
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  getPaymentMethod(): string {
    const paymentModes: { [key: number]: string } = {
      1: 'Cash',
      2: 'Card',
      3: 'Online',
      4: 'Wallet'
    };
    return paymentModes[this.data.data.paymentMode || 0] || 'Unknown';
  }

  getVendorType(vendorType?: number): string {
    if (!vendorType) return '---';
    const vendorTypes: { [key: number]: string } = {
      1: 'E-commerce Vendor',
      2: 'Instant Vendor'
    };
    return vendorTypes[vendorType] || '---';
  }

  getAddressType(): string {
    const addressTypes: { [key: number]: string } = {
      1: 'Home',
      2: 'Office',
      3: 'Other'
    };
    return addressTypes[this.data.data.deliveryAddress?.addressType || 0] || '---';
  }

  openGoogleMaps(): void {
    const lat = this.data.data.deliveryAddress?.latitude;
    const lng = this.data.data.deliveryAddress?.longitude;
    if (lat && lng) {
      window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
    }
  }

  onStatusChange(event: MatSelectChange): void {
    const previousStatus = this.data.data.status;
    this.data.data.status = event.value;
    this.handleStatusChange(event.value, this.data.data._id || '', previousStatus);
  }

  private handleStatusChange(
    status: number,
    id: string,
    previousStatus?: number
  ): void {
    let body: orderUpdatePayload = {};
    if (status === Number(numberConst.TWELVE)) {
      this._orderCancelDialogService
        .requestAdminCancellation({
          orderNo: String(this.data.data.orderNo),
          subOrderNo: String(this.data.data.subOrderNo)
        })
        .pipe(takeUntil(this.destroy$))
        .subscribe(result => {
          if (result) {
            body = {
              status: status,
              nextStatus: this.nextStatus || 0,
              rejectionReason: result
            };
            this.updateOrder(id, body);
          } else if (previousStatus !== undefined) {
            this.data.data.status = previousStatus;
          }
        });
    } else if (status === Number(numberConst.TWO)) {
      const dialogRef = this.dialog.open(ExpectedDeliveryDateComponent);
      dialogRef
        .afterClosed()
        .pipe(takeUntil(this.destroy$))
        .subscribe(result => {
          if (result) {
            body = {
              status: status,
              nextStatus: this.nextStatus || 0,
              expectedDeliveryDate: result
            };
            this.updateOrder(id, body);
          }
        });
    } else {
      body = {
        status: status,
        nextStatus: this.nextStatus || 0,
      };
      this.updateOrder(id, body);
    }
  }

  updateOrder(id: string, body: orderUpdatePayload): void {
    console.log('body updateOrder',body)
    this._spinnerService.addToLoader('updateOrder');
    this._orderService
      .updateOrder(id, body)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this._spinnerService.removeFromLoader('updateOrder');
          // Update local order status
          this.data.data.status = body.status || this.data.data.status;
          // Refresh status history
          this.initializeStatusHistory();
        },
        error: () => {
          this._spinnerService.removeFromLoader('updateOrder');
        }
      });
  }

  getValueByKey(id: number): string {
    for (const [key, value] of Object.entries(orderStatusKeysConst)) {
      if (value === id) {
        return key;
      }
    }
    return 'Unknown';
  }

  getStatusValue(order: any): number {
    return order?.status || 0;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
